import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SectionHead } from "./Home";
import { Send, Sparkles, MessageCircle } from "lucide-react";

export default function ProductRequest() {
  const [form, setForm] = useState({
    product_name: "", language: "jp", quantity: 1, budget: "", notes: "", contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/product-requests", {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        budget: form.budget ? parseFloat(form.budget) : null,
      });
      setSent(true);
      toast.success("Richiesta inviata!");
    } catch { toast.error("Errore, riprova"); }
    finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 mx-auto rounded-full border border-[#C9A227] flex items-center justify-center text-[#C9A227] mb-6">
          <Sparkles size={26} />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white">Richiesta ricevuta</h1>
        <p className="text-[#9CA3AF] mt-3 max-w-lg mx-auto leading-relaxed">
          Se riusciamo a trovare il prodotto che hai richiesto, ti invieremo un preventivo direttamente su
          <span className="text-[#C9A227] font-semibold"> WhatsApp </span>
          al numero che ci hai lasciato. Nessuna spam call, solo la nostra offerta.
        </p>
        <button
          data-testid="request-again"
          onClick={() => { setSent(false); setForm({ product_name: "", language: "jp", quantity: 1, budget: "", notes: "", contact_phone: "" }); }}
          className="btn-ghost-gold mt-6"
        >
          Nuova richiesta
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
      <SectionHead
        eyebrow="Richieste Prodotti"
        title="Non trovi quello che cerchi?"
        subtitle="Dicci cosa stai cercando e proveremo a trovarlo per te."
        icon={<Sparkles size={16} />}
      />

      {/* WhatsApp notice */}
      <div className="mt-6 border border-[#C9A227] bg-[#C9A227]/8 rounded-sm p-4 flex items-start gap-3">
        <MessageCircle size={20} className="text-[#C9A227] shrink-0 mt-0.5" />
        <div>
          <div className="font-mono-ps text-[#C9A227] text-xs tracking-widest uppercase">Come funziona</div>
          <p className="text-sm text-[#D1D5DB] mt-1 leading-relaxed">
            Compila il modulo lasciandoci il tuo numero di cellulare. Se troviamo il prodotto che hai richiesto,
            riceverai un <span className="text-[#C9A227] font-semibold">preventivo su WhatsApp</span> al numero indicato,
            di solito entro 24-48 ore.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 border border-[#C9A227]/20 rounded-sm p-6 md:p-8 bg-[#0b0b0d] space-y-4">
        <Field label="Prodotto / carta">
          <input data-testid="req-product" required value={form.product_name}
            onChange={(e) => setForm({ ...form, product_name: e.target.value })}
            placeholder="Es. Booster Box One Piece OP-09 JP, Charizard ex 199/165…"
            className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none text-base"/>
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Lingua">
            <select data-testid="req-language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
              className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none">
              <option value="jp">Giapponese</option>
              <option value="en">Inglese</option>
              <option value="it">Italiano</option>
              <option value="other">Altro</option>
            </select>
          </Field>
          <Field label="Quantità">
            <input data-testid="req-qty" type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none"/>
          </Field>
          <Field label="Budget (€)">
            <input data-testid="req-budget" type="number" step="0.01" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
              placeholder="Opzionale"
              className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none"/>
          </Field>
        </div>
        <Field label="Note aggiuntive">
          <textarea data-testid="req-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Condizione preferita, urgenza, preferenze specifiche…"
            className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none resize-none"/>
        </Field>
        <Field label="Il tuo numero WhatsApp">
          <input
            data-testid="req-phone"
            required
            type="tel"
            inputMode="tel"
            pattern="^[+0-9\s]{7,20}$"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            placeholder="Es. +39 331 425 0452"
            className="w-full bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none text-base"
          />
        </Field>
        <p className="text-[10px] font-mono-ps text-[#9CA3AF] tracking-widest uppercase">
          Usato solo per inviarti il preventivo su WhatsApp. Nessuno spam.
        </p>
        <button data-testid="req-submit" type="submit" disabled={busy} className="btn-gold w-full justify-center disabled:opacity-60">
          <Send size={16} /> {busy ? "Invio…" : "Invia richiesta"}
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    {children}
  </label>
);
